import { useGetAllSceneInsights } from "@/api/studio/insights/queries";
import { ReportsLayout } from "@/components/layouts";
import { ReportsSceneToolbar } from "@/features/reports/components/reports-scene-toolbar";
import { ReportSceneSkeleton } from "@/features/reports/components/reports-scene-skeleton";
import { SceneComparisonColumn } from "@/features/reports/components/scene-comparison-column";
import { useReportFiltersStore } from "@/features/reports/stores/use-report-filters-store";
import { useReportsSceneStore } from "@/features/reports/stores/use-reports-scene-store";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { getSceneInfoModal } from "@/features/reports/utils/get-scene-info-modal";
import { VisibilityInfoModal } from "@/features/reports/modals/visibility-info-modal";

/* Prop Types */
interface ReportsSceneOverviewRouteProps {
  projectId: string;
  studyId: string;
}

/* Constants */
const MAX_COLUMNS = 3;

/*
 * ReportsSceneOverviewRoute component
 *
 * This provides a UI to show an overview of the scene report for a specific study.
 * Displays up to 3 scenes side by side in a comparison view with dropdowns to select
 * which scenes to compare.
 */
export const ReportsSceneOverviewRoute = ({ projectId, studyId }: ReportsSceneOverviewRouteProps) => {
  /* State */
  // Track selected scenes in each column (up to 3)
  const [selectedSceneKeys, setSelectedSceneKeys] = useState<(string | null)[]>([null, null, null]);

  /* Stores */
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const metric = useReportsSceneStore((state) => state.metric);
  const theme = useGlobalStore((state) => state.theme);

  /* Queries */
  const { data: allScenes, isLoading: isAllScenesLoading } = useGetAllSceneInsights({
    projectId,
    studyId,
    orderBy: metric,
    sortBy,
  });

  /* Hooks */
  const { segmentPageEvent, segmentTrackEvent } = useSegment();
  const [, setLocation] = useLocation();
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "reports/overview");

  /* Helpers */
  // Get display name for metric
  const getDisplayName = () => {
    switch (metric) {
      case "vis":
        return "Visibility";
      case "pop":
        return "Probability of Perception (POP)";
      case "soa":
        return "Share of Attention (SOA)";
      case "pow":
        return "Attention Power Factor (POW)";
      default:
        return "Visibility";
    }
  };

  /* Handlers */
  const handleSelectScene = (columnIndex: number, sceneKey: string) => {
    setSelectedSceneKeys((prev) => {
      // Segment tracking
      segmentTrackEvent("Asset Comparison Changed", {
        projectId,
        studyId,
        position: columnIndex,
        from: prev[columnIndex],
        to: sceneKey,
        metric,
        studyType: "scene",
      });

      const newKeys = [...prev];
      newKeys[columnIndex] = sceneKey;
      return newKeys;
    });
  };

  const handleSceneImageClick = (sceneKey: string) => {
    // Segment tracking
    segmentTrackEvent("Asset Report Viewed", {
      projectId,
      studyId,
      assetId: sceneKey,
      reportType: metric,
      studyType: "scene",
    });

    // Navigate to the in-depth view
    setLocation(`/projects/${projectId}/studies/${studyId}/scene/reports/in-depth/${sceneKey}`);
  };

  /* Effects */
  // Initialize selected scenes on first load
  useEffect(() => {
    if (allScenes && allScenes.length > 0) {
      const isInitialLoad = selectedSceneKeys.every((key) => key === null);
      if (isInitialLoad) {
        const initialKeys = allScenes.slice(0, MAX_COLUMNS).map((scene) => scene.key);
        while (initialKeys.length < MAX_COLUMNS) {
          initialKeys.push(null as unknown as string);
        }
        setSelectedSceneKeys(initialKeys);
      }
    }
  }, [allScenes]);

  // Track page view
  useEffect(() => {
    if (theme) {
      segmentPageEvent("Study Report", {
        title: `${getDisplayName()} Report | Dragonfly AI Studio`,
        theme: theme,
      });
    }
  }, [theme]);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar>
      {/* Loading State */}
      {isAllScenesLoading && <ReportSceneSkeleton title={getDisplayName()} infoModal={getSceneInfoModal(metric) || <VisibilityInfoModal />} />}

      {/* Loaded Content */}
      {!isAllScenesLoading && allScenes && (
        <>
          {/* Toolbar */}
          <ReportsSceneToolbar projectId={projectId} studyId={studyId} />

          {/* Comparison Grid - 2 columns on smaller screens, 3 on larger */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 overflow-y-auto">
            {selectedSceneKeys.map((sceneKey, index) => (
              <SceneComparisonColumn
                key={index}
                projectId={projectId}
                studyId={studyId}
                allScenes={allScenes}
                selectedSceneKey={sceneKey}
                onSelectScene={(key) => handleSelectScene(index, key)}
                onImageClick={handleSceneImageClick}
                metric={metric}
              />
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {!isAllScenesLoading && (!allScenes || allScenes.length === 0) && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-base-content/70">No scenes found for this study.</p>
          </div>
        </div>
      )}
    </ReportsLayout>
  );
};
